import { IPaymentMethodResp, PaymentMethodTypeEnum } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { MdAccountBalance, MdCreditCard } from 'react-icons/md';
import { ROUTES } from '../../../constants';
import {
  CREDIT_CARD_TYPE_DISPLAY,
  PaymentMethodStatusEnumDisplay,
  PaymentMethodTypeEnumDisplay,
} from '../../../constants/paymentMethods';
import {
  KeyValueDetail,
  LabelValueLinkItem,
} from '../../DynamicLabelValueBanner/DynamicLabelValueBanner';
import { MBadge, MBox, MFlex, MIcon } from '../chakra';
import MText from '../MText';
import { LookupTriggerContent } from './LookupTriggerContent';
import { RecordLookupPopover } from './RecordLookupPopover';
import { RecordLookupPopoverHeader } from './RecordLookupPopoverHeader';

const getPaymentMethodIcon = (type: PaymentMethodTypeEnum) => {
  switch (type) {
    case PaymentMethodTypeEnum.DIRECT_DEBIT:
    case PaymentMethodTypeEnum.ACH_CREDIT:
    case PaymentMethodTypeEnum.US_BANK_TRANSFER:
      return MdAccountBalance;
    default:
      return MdCreditCard;
  }
};

export interface PaymentMethodLookupPopoverProps {
  value: string;
  /** Used to build the detail-page link; falls back to the record's account id. */
  accountId?: string;
  displayLabel?: string;
}

export function PaymentMethodLookupPopover({
  value,
  accountId,
  displayLabel,
}: PaymentMethodLookupPopoverProps) {
  if (!value) {
    return null;
  }
  return (
    <RecordLookupPopover
      recordId={value}
      endpoint={`/api/paymentMethods/${value}`}
      buttonProps={{ lineClamp: 3 }}
      renderRecord={(record) => {
        const paymentMethod = record as IPaymentMethodResp;
        const {
          paymentMethodType,
          creditCardType,
          bankName,
          lastFour,
          expirationMonth,
          expirationYear,
          defaultPaymentMethod,
          status,
          createDate,
          modifyDate,
        } = paymentMethod;

        const auditItems: LabelValueLinkItem[] = [
          {
            label: 'Created Date',
            value: toDateShort(createDate),
          },
          {
            label: 'Last Modified Date',
            value: toDateShort(modifyDate),
          },
        ];

        const isCreditCard =
          paymentMethodType === PaymentMethodTypeEnum.CREDIT_CARD;
        const displayName = isCreditCard
          ? (creditCardType && CREDIT_CARD_TYPE_DISPLAY[creditCardType]) ||
            PaymentMethodTypeEnumDisplay[paymentMethodType]
          : bankName || PaymentMethodTypeEnumDisplay[paymentMethodType];
        const maskedLastFour = lastFour ? `•••• ${lastFour}` : '';
        const showExpiration = Boolean(
          isCreditCard && expirationMonth && expirationYear,
        );

        return (
          <>
            <RecordLookupPopoverHeader
              recordId={paymentMethod.id}
              href={ROUTES.getAccountPaymentMethodView(
                paymentMethod.account?.id ?? accountId ?? '',
                paymentMethod.id,
              )}
              recordName={paymentMethod.paymentMethodName ?? value}
              displayStatus={
                status ? PaymentMethodStatusEnumDisplay[status] : undefined
              }
            />
            <MFlex gap={6} px={3} pb={2}>
              {auditItems.map(({ label, value: itemValue }) => (
                <KeyValueDetail key={label} label={label} value={itemValue} />
              ))}
            </MFlex>

            <MFlex alignItems="center" gap={3} p={3} mt={2}>
              <MBox
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <MIcon
                  as={getPaymentMethodIcon(paymentMethodType)}
                  color="tGray.darkPurple"
                  boxSize={5}
                />
              </MBox>
              <MBox flex={1}>
                <MFlex alignItems="center" gap={2}>
                  <MText fontWeight="600" fontSize="sm">
                    {displayName}
                  </MText>
                  <MText color="tGray.darkPurple" fontSize="sm">
                    {maskedLastFour}
                  </MText>
                  {defaultPaymentMethod && (
                    <MBadge
                      colorPalette="green"
                      variant="subtle"
                      fontWeight="bold"
                      fontSize="2xs"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                    >
                      Default
                    </MBadge>
                  )}
                </MFlex>
                <MFlex
                  justifyContent="space-between"
                  alignItems="center"
                  mt={0.5}
                >
                  <MText fontSize="xs" color="tGray.darkPurple">
                    {PaymentMethodTypeEnumDisplay[paymentMethodType]}
                  </MText>
                  {showExpiration && (
                    <MText fontSize="xs" color="tGray.darkPurple">
                      Exp {expirationMonth?.toString().padStart(2, '0')}/
                      {expirationYear}
                    </MText>
                  )}
                </MFlex>
              </MBox>
            </MFlex>
          </>
        );
      }}
    >
      <LookupTriggerContent displayLabel={displayLabel} value={value} />
    </RecordLookupPopover>
  );
}
